import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { humanReadableSize } from '@utils/format/fileSize';
import { DriveItemMenu, type DriveItemAction } from './DriveItemMenu';
import type { DriveItem } from '@/types/file.types';

export interface DriveItemRowProps {
  item: DriveItem;
  selected: boolean;
  selectionEnabled: boolean;
  canPreview: boolean;
  canShare: boolean;
  canLibrary: boolean;
  canDownload: boolean;
  canRename: boolean;
  canMove: boolean;
  canCopyUrl: boolean;
  canViewProperties: boolean;
  canDelete: boolean;
  busy?: boolean;
  onOpenFolder: (item: DriveItem) => void;
  onToggleSelection: (item: DriveItem) => void;
  onPreview: (item: DriveItem) => void;
  onShare: (item: DriveItem) => void;
  onLibrary: (item: DriveItem) => void;
  onSecondaryAction: (item: DriveItem, action: DriveItemAction) => void;
}

function isPdf(item: DriveItem): boolean {
  return item.type === 'file' && item.path.toLowerCase().endsWith('.pdf');
}

function ItemIdentity({ item }: { item: DriveItem }) {
  const metadata = item.type === 'file'
    ? [
        item.size !== undefined ? humanReadableSize(item.size) : '',
        item.lastModified ? new Date(item.lastModified).toLocaleDateString() : '',
      ].filter(Boolean).join(' · ')
    : '';

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      {item.type === 'folder'
        ? <FolderRoundedIcon sx={{ color: '#8ff5ff', flexShrink: 0 }} />
        : <DataObjectRoundedIcon sx={{ color: '#ff51fa', flexShrink: 0 }} />
      }
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600 }} noWrap>{item.name}</Typography>
        {metadata && (
          <Typography sx={{ mt: 0.25, color: 'text.secondary', fontSize: 12 }} noWrap>
            {metadata}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * Clean drive row used by the Files view.
 *
 * The folder identity is the navigation control. Frequently used actions stay
 * at the right edge, while management actions remain in DriveItemMenu. Actions
 * are always present in the accessibility tree: pointer users reveal them on
 * hover, keyboard users on focus-within, and touch/small-screen users see them
 * without relying on hover.
 */
export function DriveItemRow({
  item,
  selected,
  selectionEnabled,
  canPreview,
  canShare,
  canLibrary,
  canDownload,
  canRename,
  canMove,
  canCopyUrl,
  canViewProperties,
  canDelete,
  busy = false,
  onOpenFolder,
  onToggleSelection,
  onPreview,
  onShare,
  onLibrary,
  onSecondaryAction,
}: DriveItemRowProps) {
  const pdf = isPdf(item);
  const previewable = canPreview && (item.type === 'folder' || pdf);

  const handleMenuAction = (action: DriveItemAction) => {
    if (action === 'library') {
      onLibrary(item);
      return;
    }
    onSecondaryAction(item, action);
  };

  return (
    <Card
      className="glass-card"
      data-testid={`drive-item-row-${item.path}`}
      sx={{
        borderRadius: 2,
        borderColor: selected ? '#8ff5ff' : undefined,
        boxShadow: selected ? '0 0 0 1px #8ff5ff inset' : undefined,
        '& .DriveItemRow-actions, & .DriveItemRow-selection': {
          opacity: { xs: 1, sm: selected ? 1 : 0 },
          pointerEvents: { xs: 'auto', sm: selected ? 'auto' : 'none' },
          transform: { xs: 'none', sm: selected ? 'none' : 'translateX(4px)' },
          transition: 'opacity 140ms ease, transform 140ms ease',
        },
        '&:hover .DriveItemRow-actions, &:hover .DriveItemRow-selection, &:focus-within .DriveItemRow-actions, &:focus-within .DriveItemRow-selection': {
          opacity: 1,
          pointerEvents: 'auto',
          transform: 'none',
        },
        '@media (hover: none)': {
          '& .DriveItemRow-actions, & .DriveItemRow-selection': {
            opacity: 1,
            pointerEvents: 'auto',
            transform: 'none',
          },
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          display: 'flex',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          columnGap: { xs: 1, sm: 1.5 },
          rowGap: 1,
        }}
      >
        {selectionEnabled && (
          <Checkbox
            className="DriveItemRow-selection"
            size="small"
            disableRipple
            checked={selected}
            disabled={busy}
            inputProps={{ 'aria-label': `Select ${item.name}` }}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onToggleSelection(item)}
            sx={{
              p: 0.5,
              flexShrink: 0,
              color: 'rgba(143,245,255,0.5)',
              '&.Mui-checked': { color: '#8ff5ff' },
            }}
          />
        )}

        {item.type === 'folder' ? (
          <ButtonBase
            aria-label={`Open folder ${item.name}`}
            disableRipple
            onClick={() => onOpenFolder(item)}
            sx={{
              minWidth: 0,
              flex: 1,
              justifyContent: 'flex-start',
              borderRadius: 1,
              textAlign: 'left',
              '&.Mui-focusVisible': {
                outline: '2px solid #8ff5ff',
                outlineOffset: 3,
              },
            }}
          >
            <ItemIdentity item={item} />
          </ButtonBase>
        ) : (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <ItemIdentity item={item} />
          </Box>
        )}

        <Stack
          className="DriveItemRow-actions"
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
            pl: { xs: selectionEnabled ? 5 : 4, sm: 0 },
          }}
        >
          {previewable && (
            <Button
              size="small"
              variant="outlined"
              disableRipple
              startIcon={<VisibilityRoundedIcon />}
              disabled={busy}
              aria-label={`Preview ${item.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onPreview(item);
              }}
              sx={{ minWidth: 88, px: 1 }}
            >
              Preview
            </Button>
          )}

          {canShare && (
            <Tooltip title={`Share ${item.name}`}>
              <span>
                <IconButton
                  size="small"
                  disableRipple
                  disabled={busy}
                  aria-label={`Share ${item.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onShare(item);
                  }}
                  sx={{ border: '1px solid rgba(143,245,255,0.35)', borderRadius: 1 }}
                >
                  <IosShareRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <DriveItemMenu
            itemName={item.name}
            isFolder={item.type === 'folder'}
            isPdf={pdf}
            canOpenLibrary={item.type === 'folder' && canLibrary}
            canDownload={item.type === 'file' && canDownload}
            canRename={canRename}
            canMove={canMove}
            canCopyUrl={canCopyUrl}
            canViewProperties={item.type === 'file' && canViewProperties}
            canDelete={canDelete}
            disabled={busy}
            onAction={handleMenuAction}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
